import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AddProductModal from '../components/AddProductModal';
import EditProductModal from '../components/EditProductModal';
import DeleteConfirmationModal from '../components/DeleteConfirmationModal';
import './ProductsPage.css';

// Configurazione API
const API_BASE_URL = process.env.NODE_ENV === 'production' 
  ? '/shop/backend/api' 
  : 'http://efwypvu.cluster021.hosting.ovh.net/shop/backend/api';

const ProductsPage = () => {
  const navigate = useNavigate();
  const publicUrl = process.env.PUBLIC_URL || '';
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [productToDelete, setProductToDelete] = useState(null);
  
  // Stati per i filtri
  const [filters, setFilters] = useState({
    searchText: '',
    sortBy: 'data_creazione', // 'data_creazione', 'prezzo', 'titolo'
    sortOrder: 'desc' // 'asc', 'desc'
  });

  useEffect(() => {
    checkAuth();
    loadProducts();
    loadCategories();
  }, []);

  // Applica filtri quando cambiano i prodotti o i filtri
  useEffect(() => {
    applyFilters();
  }, [products, filters]);

  const checkAuth = () => {
    console.log('🔍 Verifica autenticazione locale...');
    
    const isLoggedIn = localStorage.getItem('admin_logged_in');
    const adminData = localStorage.getItem('admin_data');
    
    if (!isLoggedIn || !adminData) {
      console.log('❌ Non autenticato, reindirizzamento al login...');
      navigate('/amministrazione');
      return;
    }
    
    console.log('✅ Autenticazione verificata!');
    setLoading(false);
  };

  const loadProducts = async () => {
    try {
      console.log('📦 Caricamento prodotti...');
      
      const response = await fetch(`${API_BASE_URL}/prodotti.php`);
      const data = await response.json();
      
      console.log('📋 Prodotti ricevuti:', data);
      
      if (data.success) {
        const productsData = data.data || [];
        
        // Carica le immagini per ogni prodotto che ha una cartella foto
        const productsWithImages = await Promise.all(
          productsData.map(async (product) => {
            // Normalizza attivo a boolean (può arrivare come "0"/"1")
            product.attivo = product.attivo === 1 || product.attivo === '1' || product.attivo === true;
            product.quantita = parseInt(product.quantita ?? 0, 10);
            if (product.foto) {
              try {
                const imgResponse = await fetch(
                  `${API_BASE_URL}/get_images.php?folder=${product.foto}`
                );
                const imgData = await imgResponse.json();
                
                if (imgData.success && imgData.data.main_image) {
                  product.mainImageUrl = imgData.data.main_image.full_url;
                }
              } catch (err) {
                console.warn(`⚠️ Errore caricamento immagini prodotto ${product.titolo}:`, err);
              }
            }
            return product;
          })
        );
        
        setProducts(productsWithImages);
      } else {
        console.error('❌ Errore caricamento prodotti:', data.error);
      }
    } catch (err) {
      console.error('💥 Errore caricamento prodotti:', err);
    }
  };

  const loadCategories = async () => {
    try {
      console.log('📁 Caricamento categorie...');
      
      const response = await fetch(`${API_BASE_URL}/categorie.php`);
      const data = await response.json();
      
      console.log('📋 Categorie ricevute:', data);
      
      if (data.success) {
        setCategories(data.data || []);
      } else {
        console.error('❌ Errore caricamento categorie:', data.error);
      }
    } catch (err) {
      console.error('💥 Errore caricamento categorie:', err);
    }
  };

  const handleAddProduct = async (productData) => {
    console.log('📦 Aggiunta prodotto:', productData);
    
    try {
      const response = await fetch(`${API_BASE_URL}/prodotti.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(productData),
      });

      const data = await response.json();
      console.log('📋 Risposta aggiunta prodotto:', data);

      if (!data.success) {
        throw new Error(data.error || 'Errore durante l\'aggiunta del prodotto');
      }

      console.log('✅ Prodotto aggiunto con successo!');
      loadProducts(); // Ricarica la lista
      setShowAddModal(false);
      
    } catch (err) {
      console.error('💥 Errore aggiunta prodotto:', err);
      throw err;
    }
  };

  const handleEditProduct = async (productData) => {
    console.log('✏️ Modifica prodotto:', productData);
    
    try {
      const response = await fetch(`${API_BASE_URL}/prodotti.php`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(productData),
      });

      const data = await response.json();
      console.log('📋 Risposta modifica prodotto:', data);

      if (!data.success) {
        throw new Error(data.error || 'Errore durante la modifica del prodotto');
      }

      console.log('✅ Prodotto modificato con successo!');
      loadProducts(); // Ricarica la lista
      setShowEditModal(false);
      setEditingProduct(null);
      
    } catch (err) {
      console.error('💥 Errore modifica prodotto:', err);
      throw err;
    }
  };

  const handleDeleteProduct = async (productId) => {
    console.log('🗑️ Eliminazione prodotto:', productId);
    
    try {
      // 1. Trova il prodotto per ottenere il nome della cartella foto
      const product = products.find(p => p.id_prodotto === productId);
      const folderName = product?.foto;
      
      // 2. Elimina il prodotto dal database
      const response = await fetch(`${API_BASE_URL}/prodotti.php`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ id: productId }),
      });

      const data = await response.json();
      console.log('📋 Risposta eliminazione prodotto:', data);

      if (!data.success) {
        throw new Error(data.error || 'Errore durante l\'eliminazione del prodotto');
      }

      console.log('✅ Prodotto eliminato con successo dal database!');
      
      // 3. Elimina la cartella immagini se esiste
      if (folderName) {
        console.log('📸 Eliminazione cartella immagini:', folderName);
        try {
          const deleteImagesResponse = await fetch(`${API_BASE_URL}/delete_images.php`, {
            method: 'DELETE',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ folder: folderName }),
          });

          const deleteImagesData = await deleteImagesResponse.json();
          console.log('📋 Risposta eliminazione immagini:', deleteImagesData);

          if (deleteImagesData.success) {
            console.log('✅ Cartella immagini eliminata con successo!');
          } else {
            console.warn('⚠️ Impossibile eliminare la cartella immagini:', deleteImagesData.error);
          }
        } catch (imgErr) {
          console.warn('⚠️ Errore durante l\'eliminazione delle immagini:', imgErr);
          // Non bloccare il processo se l'eliminazione delle immagini fallisce
        }
      }
      
      console.log('✅ Prodotto e immagini eliminate con successo!');
      loadProducts(); // Ricarica la lista
      setShowDeleteModal(false);
      setProductToDelete(null);
      
    } catch (err) {
      console.error('💥 Errore eliminazione prodotto:', err);
      throw err;
    }
  };

  const openEditModal = (product) => {
    setEditingProduct(product);
    setShowEditModal(true);
  };

  const openDeleteModal = (product) => {
    setProductToDelete(product);
    setShowDeleteModal(true);
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR'
    }).format(price);
  };

  const getCategoryName = (categoryId) => {
    const category = categories.find(cat => cat.id_categoria === categoryId);
    return category ? category.nome : 'N/A';
  };

  // Funzione per applicare i filtri
  const applyFilters = () => {
    let filtered = [...products];

    // Filtro per testo di ricerca
    if (filters.searchText.trim()) {
      const searchTerm = filters.searchText.toLowerCase();
      filtered = filtered.filter(product => {
        const productName = product.titolo?.toLowerCase() || '';
        const productDesc = product.descrizione?.toLowerCase() || '';
        const categoryName = getCategoryName(product.id_categoria).toLowerCase();
        
        return productName.includes(searchTerm) || 
               productDesc.includes(searchTerm) || 
               categoryName.includes(searchTerm);
      });
    }

    // Ordinamento
    filtered.sort((a, b) => {
      let aValue, bValue;

      switch (filters.sortBy) {
        case 'titolo':
          aValue = a.titolo?.toLowerCase() || '';
          bValue = b.titolo?.toLowerCase() || '';
          break;
        case 'prezzo':
          aValue = parseFloat(a.prezzo) || 0;
          bValue = parseFloat(b.prezzo) || 0;
          break;
        case 'data_creazione':
          aValue = new Date(a.data_creazione) || new Date(0);
          bValue = new Date(b.data_creazione) || new Date(0);
          break;
        default:
          return 0;
      }

      if (filters.sortOrder === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    setFilteredProducts(filtered);
  };

  // Funzioni per gestire i filtri
  const handleSearchChange = (e) => {
    setFilters(prev => ({
      ...prev,
      searchText: e.target.value
    }));
  };

  const handleSortChange = (e) => {
    const [sortBy, sortOrder] = e.target.value.split('-');
    setFilters(prev => ({
      ...prev,
      sortBy: sortBy,
      sortOrder: sortOrder
    }));
  };

  const resetFilters = () => {
    setFilters({
      searchText: '',
      sortBy: 'data_creazione',
      sortOrder: 'desc'
    });
  };

  const getProductImage = (product) => {
    // Se il prodotto ha mainImageUrl (caricato da get_images.php)
    if (product.mainImageUrl) {
      return product.mainImageUrl;
    }
    // Placeholder se non ci sono immagini
    return '/placeholder-product.png';
  };

  if (loading) {
    return (
      <div className="products-page">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="products-page admin-page">
      {/* Sidebar */}
      <aside className="admin-sidebar">
        <div className="sidebar-header">
          <img src={`${publicUrl}/logomuseo.png`} alt="Museo Stibbert" className="sidebar-logo" />
      
        </div>

        <div className="sidebar-admin-title">
          <h1><strong style={{color: 'var(--bianco), font-weight: 700, font-size: 1.2rem'}}>Museo Stibbert</strong> Amministrazione</h1>
        </div>

        <nav className="sidebar-nav">
          <button className="nav-item" onClick={() => navigate('/amministrazione/dashboard')}>
            <i className="fas fa-tachometer-alt"></i>
            <span>Dashboard</span>
          </button>
          <button className="nav-item" onClick={() => navigate('/amministrazione/categorie')}>
            <i className="fas fa-tags"></i>
            <span>Categorie</span>
          </button>
          <button className="nav-item active">
            <i className="fas fa-box"></i>
            <span>Prodotti</span>
          </button>
          <button className="nav-item" onClick={() => navigate('/amministrazione/ordini')}>
            <i className="fas fa-shopping-cart"></i>
            <span>Ordini</span>
          </button>
          <button className="nav-item" onClick={() => navigate('/amministrazione/impostazioni')}>
            <i className="fas fa-cog"></i>
            <span>Impostazioni</span>
          </button>
        </nav>

        <div className="sidebar-footer">
          <button className="nav-item logout-item" onClick={() => {
            localStorage.removeItem('admin_logged_in');
            localStorage.removeItem('admin_data');
            navigate('/amministrazione');
          }}>
            <i className="fas fa-sign-out-alt"></i>
            <span>Logout</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="admin-main">
        <div className="page-header">
          <div className="header-content">
            <h1>Prodotti</h1>
            <div className="header-actions">
              <button 
                className="btn btn-primary"
                onClick={() => setShowAddModal(true)}
              >
                <i className="fas fa-plus"></i>
                Aggiungi Prodotto
              </button>
            </div>
          </div>
        </div>

        <div className="main-content">
          {/* Filtri */}
          <div className="filters-bar">
            {/* Ricerca per testo - Sinistra */}
            <div className="search-group">
              <input
                id="search-input"
                type="text"
                placeholder="Cerca per nome, categoria o descrizione..."
                value={filters.searchText}
                onChange={handleSearchChange}
                className="search-input"
              />
            </div>

            {/* Ordinamento e Reset - Destra */}
            <div className="controls-group">
              <div className="sort-group">
                <label htmlFor="sort-select">Ordina per:</label>
                <select
                  id="sort-select"
                  className="sort-select"
                  value={`${filters.sortBy}-${filters.sortOrder}`}
                  onChange={handleSortChange}
                >
                  <option value="data_creazione-desc">Data (Più Recenti)</option>
                  <option value="data_creazione-asc">Data (Più Vecchi)</option>
                  <option value="prezzo-asc">Prezzo (Crescente)</option>
                  <option value="prezzo-desc">Prezzo (Decrescente)</option>
                  <option value="titolo-asc">Nome (A-Z)</option>
                  <option value="titolo-desc">Nome (Z-A)</option>
                </select>
              </div>
              <button className="reset-filters-btn" onClick={resetFilters}>
                Reset Filtri
              </button>
            </div>
          </div>

          {/* Tabella Prodotti */}
          <div className="table-container">
            <table className="products-table">
              <thead>
                <tr>
                  <th>PRODOTTO</th>
                  <th>QUANTITÀ</th>
                  <th>PREZZO</th>
                  <th>STATO</th>
                  <th>AZIONI</th>
                </tr>
              </thead>
              <tbody>
                {filteredProducts.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="empty-state">
                      <div className="empty-message">
                        <i className="fas fa-box-open"></i>
                        <p>Nessun prodotto trovato</p>
                        <button 
                          className="btn btn-primary"
                          onClick={() => setShowAddModal(true)}
                        >
                          Aggiungi il primo prodotto
                        </button>
                      </div>
                    </td>
                  </tr>
                ) : (
                  filteredProducts.map((product) => (
                    <tr key={product.id_prodotto}>
                      <td className="admin-product-info">
                        <div className="product-id">#{product.id_prodotto}</div>
                        <div className="admin-product-image">
                          <img src={getProductImage(product)} alt={product.titolo} />
                        </div>
                        <span className="product-title">{product.titolo}</span>
                      </td>
                      <td className="quantity-cell">
                        <span className={`quantity-badge ${product.quantita > 0 ? 'in-stock' : 'out-stock'}`}>
                          {product.quantita > 0 ? product.quantita : '0'}
                        </span>
                      </td>
                      <td className="price-cell">
                        <span className="price">{formatPrice(product.prezzo)}</span>
                      </td>
                      <td className="status-cell">
                        <span className={`status-badge ${product.attivo ? 'active' : 'inactive'}`}>
                          {product.attivo ? 'Attivo' : 'Non Attivo'}
                        </span>
                      </td>
                      <td className="actions-cell">
                        <button 
                          className="btn-edit"
                          onClick={() => openEditModal(product)}
                          title="Modifica prodotto"
                        >
                          <i className="fas fa-edit"></i>
                        </button>
                        <button 
                          className="btn-delete"
                          onClick={() => openDeleteModal(product)}
                          title="Elimina prodotto"
                        >
                          <i className="fas fa-trash"></i>
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </main>

      {/* Modals */}
      <AddProductModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onAddProduct={handleAddProduct}
        categories={categories}
      />

      <EditProductModal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setEditingProduct(null);
        }}
        onEditProduct={handleEditProduct}
        product={editingProduct}
        categories={categories}
      />

      <DeleteConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setProductToDelete(null);
        }}
        onConfirm={() => handleDeleteProduct(productToDelete?.id_prodotto)}
        title="Conferma Eliminazione"
        message={`Sei sicuro di voler eliminare il prodotto "${productToDelete?.titolo}"?`}
        confirmText="Elimina Prodotto"
      />
    </div>
  );
};

export default ProductsPage;
